<script setup lang="ts">
//#region import
import { ref , computed, onBeforeMount } from 'vue';
import { useTypedStore } from '@/store';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
//#endregion

//#region types
export interface Props {
  label?: string;
  propCurrencyCode?: string,
} 
//#endregion

//#region define
// withDefaults(defineProps<Props>(), {
//     label: '',
// });
const props = withDefaults(defineProps<Props>(), {
  label: '',
  propCurrencyCode: '',
});
//#endregion

//#region composables
const store = useTypedStore();
const {
  formatMetric,
  currencyCode,
} = useFormatCurrency();
//#endregion

//#region state
const modelValue = defineModel<string>();
const selectValue = ref(modelValue.value);
const searchValue = ref('');
//#endregion

//#region computed
const reportsSettings = computed(() => store.getters['reports/reportsSettings']);

const columnGroup = computed(() => {
  return reportsSettings.value.grouped_metrics;
});

const optionsSelect = computed(() => {
  type SubItemsNameType = typeof columnGroup.value[number]['items'][number]['name'];
  const res = columnGroup.value.map((elem) => {
    const itemsList = elem.items.reduce((acc, el) => {
      acc.push(el.name);
      if (el.items!.length) {
        el.items!.forEach((subItems) => acc.push(subItems.name));
      }
      return acc;
    }, [] as SubItemsNameType[]);

    const options = itemsList
      .map((metric) => ({
        value: metric,
        label: getTranslateMetrics(metric)?.title,
        description: getTranslateMetrics(metric)?.description,
      }))
      .filter(
        (opt) => opt.label.toLowerCase().indexOf(searchValue.value.toLowerCase())
            > -1
          || opt.description
            .toLowerCase()
            .indexOf(searchValue.value.toLowerCase()) > -1,
      );

    return {
      label: getTranslateGroup(elem.group_name),
      options,
    };
  });

  return res.filter((f) => f.options.length);
});
//#endregion

//#region methods
function visibleChange() {
  setTimeout(() => (searchValue.value = ''), 150);
}

function changeSelect(value: string) {
  modelValue.value = value;
}

function getTranslateMetrics(metric: string) {
  return formatMetric(reportsSettings.value.metrics.find((e) => e.column_name === metric));
}

function getTranslateGroup(groupName: string) {
  return reportsSettings.value.metrics_groups.find(
    (e) => e.group_name === groupName,
  )!.title;
}
//#endregion

//#region hooks
onBeforeMount(() => {
  currencyCode.value = props.propCurrencyCode;
  if (!modelValue.value) {
    selectValue.value = optionsSelect.value[0]!.options[0]!.value;
    changeSelect(selectValue.value);
  }
});
//#endregion
</script>

<template>
  <div class="select-metrics">
    <UiSelectElement
      v-model="selectValue"
      :placeholder="$t('affiliates.filters.introducedBy')"
      :label="label"
      filterable
      size="big"
      width=""
      class="select-metrics__select"
      popper-class="select-metrics__popper"
      @change="changeSelect"
      @visible-change="visibleChange"
    >
      <template v-slot:options>
        <ElOptionGroup
          v-for="group in optionsSelect"
          :key="group.label"
          :label="group.label"
        >
          <ElOption
            v-for="item in group.options"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          >
            <span class="t:ml-16">{{ item.label }}</span>
            <span class="select-metrics__description">({{ item.description }})</span>
          </ElOption>
        </ElOptionGroup>
      </template>
    </UiSelectElement>
  </div>
</template>

<style lang="scss">
.select-metrics {
  width: 100%;
  &__popper {
    .el-select-dropdown__item{
      display: flex;
      align-items: center;
    }
    .el-select-group__wrap{
      padding-bottom: 24px;
      &:not(:last-of-type):after {
        content: "";
        position: absolute;
        display: block;
        left: 20px;
        right: 20px;
        bottom: 12px;
        height: 1px;
        background: #e4e7ed;
      }
    }
  }

  &__description {
    float: right;
    color: var(--text-body-tertiary);
    font-size: 11px;
    font-weight: 400;
    margin-left: 10px;
    display: flex;
    align-items: center;
    padding-top: 2px;
  }
}
</style>
